import { getBackendUrl } from "../lib/utils";
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Calendar, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      setError(false);
      try {
        const backendUrl = getBackendUrl();
        const res = await axios.get(`${backendUrl}/api/pages/${slug}`);
        setPage(res.data);
      } catch (err) {
        console.error("Error fetching page details", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  useEffect(() => {
    if (page) {
      document.title = page.seo_title || `${page.title} - İSİLKOOP`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.seo_description || `${page.title} aydınlatma ve bilgilendirme sayfası.`);
      }
    }
  }, [page]);

  if (loading) {
    return (
      <div className="pt-36 pb-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full border-4 border-blue-600 border-t-transparent animate-spin"></div>
          <span className="text-sm font-semibold text-slate-500">Sayfa yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="pt-36 pb-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="max-w-md text-center p-8 bg-white border border-slate-200/80 rounded-3xl shadow-sm">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Sayfa Bulunamadı</h2>
          <p className="text-sm text-slate-500 mb-6">Ulaşmaya çalıştığınız sayfa kaldırılmış, adı değiştirilmiş veya geçici olarak kullanılamıyor olabilir.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-md hover:bg-blue-700 transition">
            <ArrowLeft size={16} /> Anasayfaya Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-36 pb-24 min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Back Link */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-blue-600 transition">
            <ArrowLeft size={16} /> Anasayfa
          </Link>
        </div>

        {/* Page Content Card */}
        <motion.article 
          initial={{ opacity: 0, y: 15 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5 }}
          className="bg-white border border-slate-200/80 rounded-3xl p-6 md:p-12 shadow-sm"
        >
          {/* Article Header */}
          <header className="border-b border-slate-100 pb-6 mb-8">
            <div className="flex items-center gap-3 text-xs font-bold text-blue-600 uppercase tracking-wider mb-3">
              <ShieldCheck size={16} /> Yasal Metin & Sözleşme
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight mb-4">{page.title}</h1>
            
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
              <Calendar size={14} />
              <span>Son Güncelleme: {page.updated_at ? new Date(page.updated_at).toLocaleDateString('tr-TR') : ''}</span>
            </div>
          </header>

          {/* HTML Render Body */}
          <div 
            className="prose prose-slate max-w-none prose-headings:font-extrabold prose-p:leading-relaxed prose-p:text-slate-600 prose-p:mb-5 prose-headings:text-slate-800 prose-strong:text-slate-900 prose-ul:list-disc prose-ul:pl-5 prose-li:text-slate-600 prose-li:mb-2 text-sm leading-relaxed" 
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </motion.article>
      </div>
    </div>
  );
};

export default DynamicPage;
